import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Button, Checkbox, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { StyledSwitch } from './FacilityControlStyled';
import { CATEGORY_CARDS, cardOf } from './iotChannels';
import LightingIcon from '../../../_icons/LightingIcon';
import ThermometerIcon from '../../../_icons/ThermometerIcon';
import DisplayControlIcon from '../../../_icons/DisplayControlIcon';
import DumbbellIcon from '../../../_icons/DumbbellIcon';
import EtcIcon from '../../../_icons/EtcIcon';

const CARD_ICON = { LIGHT: LightingIcon, CLIMATE: ThermometerIcon, DISPLAY: DisplayControlIcon, EQUIPMENT: DumbbellIcon, ETC: EtcIcon };

const Container = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '460px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  borderRadius: '8px',
  overflow: 'hidden',
  outline: 'none',
});

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '48px',
  padding: '0 16px 0 24px',
  backgroundColor: '#262626',
});

const Title = styled(Typography)({ fontSize: '16px', fontWeight: 600, color: '#fff' });

const Body = styled(Box)({ padding: '16px 24px 8px', overflowY: 'auto', backgroundColor: '#fafbfc', flex: 1 });

const Sub = styled(Typography)({ fontSize: '13px', color: '#808080', marginBottom: '8px' });

// 행 구분선은 아래쪽 한 줄만 (위·아래 둘 다 주면 이중선이 된다)
const Row = styled(Box, { shouldForwardProp: p => p !== 'disabledRow' })(({ disabledRow }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '48px',
  borderBottom: '1px solid #EEE',
  opacity: disabledRow ? 0.45 : 1,
  '&:last-of-type': { borderBottom: 'none' },
}));

const AllRow = styled(Row)({ borderBottom: '2px solid #E6E6E6', marginBottom: '4px', '&:last-of-type': { borderBottom: '2px solid #E6E6E6' } });

const Label = styled(Typography, { shouldForwardProp: p => p !== 'bold' })(({ bold }) => ({
  fontSize: '15px',
  fontWeight: bold ? 600 : 500,
  color: '#333',
}));

const Left = styled(Box)({ display: 'flex', alignItems: 'center', gap: '2px', minWidth: 0 });
const Right = styled(Box)({ display: 'flex', alignItems: 'center', gap: '8px' });
const Status = styled(Typography, { shouldForwardProp: p => p !== 'active' })(({ active }) => ({
  fontSize: '13px',
  color: active ? '#4AB3BC' : '#B3B3B3',
}));
const Hint = styled(Typography)({ fontSize: '12px', color: '#999', marginLeft: '6px' });

const Empty = styled(Box)({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '28px 0 12px' });
const EmptyText = styled(Typography)({ fontSize: '14px', color: '#999' });

const OutlineButton = styled(Button)({
  height: '36px',
  borderRadius: '18px',
  border: '1px solid #4AB3BC',
  color: '#0F7F85',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  backgroundColor: '#fff',
});

const DangerOutlineButton = styled(OutlineButton)({
  border: '1px solid #E8A0A0',
  color: '#D32F2F',
});

const Footer = styled(Box)({ display: 'flex', gap: '8px', padding: '12px 24px 16px', backgroundColor: '#fafbfc' });

const PrimaryButton = styled(Button)({
  flex: 1,
  height: '44px',
  borderRadius: '8px',
  backgroundColor: '#4ab3bc',
  color: '#fff',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: '#3a9aa2' },
  '&.Mui-disabled': { backgroundColor: '#B8DADB', color: '#fff' },
});

const GhostButton = styled(Button)({
  flex: 1,
  height: '44px',
  borderRadius: '8px',
  backgroundColor: '#E6E6E6',
  color: '#666',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
});

const OptionGrid = styled(Box)({ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', padding: '4px 0 8px' });
const OptionButton = styled(Button)({
  height: '52px',
  justifyContent: 'flex-start',
  gap: '10px',
  padding: '0 14px',
  borderRadius: '10px',
  border: '1px solid #E6E6E6',
  backgroundColor: '#fff',
  color: '#333',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  '&.Mui-disabled': { opacity: 0.4 },
});

/**
 * CategoryCardDialog - 전원/카테고리 카드 다이얼로그 (매니저앱 바텀시트와 같은 규칙)
 *  - 전원 카드: 미배정 채널의 전체·개별 토글 + 이름 수정
 *  - 카테고리 카드: 넣은 채널의 전체·개별 토글 + 이름 수정 + 빼기(휴지통) + [＋ 전원 추가] 선택 저장 + 상단 [카테고리 제거]
 * @param {string} cardKey - 'POWER' | 카테고리 카드 키
 * @param {Array} rows - [{channel, label, on, disabled}]
 * @param {Array} candidates - 추가 후보 [{channel, label, otherCard}]
 */
const CategoryCardDialog = ({
  open,
  onClose,
  cardKey,
  roomName,
  rows = [],
  candidates = [],
  pending = false,
  onToggleAll,
  onToggleRow,
  onEditRow,
  onRemoveRow,
  onAddChannels,
  onRemoveCard,
}) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState([]);
  const isPower = cardKey === 'POWER';
  const title = isPower ? t('전원') : t(cardOf(cardKey)?.label || '');

  useEffect(() => {
    if (!open) {
      setAdding(false);
      setPicked([]);
    }
  }, [open, cardKey]);

  const enabledRows = rows.filter(r => !r.disabled);
  const allOn = enabledRows.length > 0 && enabledRows.every(r => r.on);
  const togglePick = ch => setPicked(prev => (prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]));

  return (
    <Modal open={open} onClose={onClose}>
      <Container>
        <Header>
          <Title>{adding ? `${title}${t('에 전원 추가')}` : title}</Title>
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Header>

        {adding ? (
          <>
            <Body>
              <Sub>{roomName}</Sub>
              {candidates.length === 0 ? (
                <EmptyText sx={{ textAlign: 'center', py: 3 }}>{t('추가할 수 있는 전원이 없습니다.')}</EmptyText>
              ) : (
                candidates.map(c => (
                  <Row key={c.channel} onClick={() => togglePick(c.channel)} sx={{ cursor: 'pointer' }}>
                    <Left>
                      <Checkbox size="small" checked={picked.includes(c.channel)} sx={{ color: '#CCC', '&.Mui-checked': { color: '#4AB3BC' } }} />
                      <Label>{c.label}</Label>
                      {c.otherCard && <Hint>{c.otherCard}{t('에서 이동')}</Hint>}
                    </Left>
                  </Row>
                ))
              )}
            </Body>
            <Footer>
              <GhostButton onClick={() => setAdding(false)}>{t('뒤로')}</GhostButton>
              <PrimaryButton
                disabled={picked.length === 0 || pending}
                onClick={() => {
                  onAddChannels(picked);
                  setPicked([]);
                  setAdding(false);
                }}>
                {pending ? t('저장 중...') : `${t('저장')} (${picked.length})`}
              </PrimaryButton>
            </Footer>
          </>
        ) : (
          <>
            <Body>
              <Sub>{roomName}</Sub>
              {rows.length === 0 ? (
                isPower ? (
                  <EmptyText sx={{ textAlign: 'center', py: 3 }}>{t('모든 전원이 카테고리에 배정되어 있습니다.')}</EmptyText>
                ) : (
                  <Empty>
                    <EmptyText>{t('지정된 전원이 없습니다.')}</EmptyText>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <OutlineButton startIcon={<AddIcon fontSize="small" />} disabled={pending} onClick={() => setAdding(true)}>
                        {t('전원 추가')}
                      </OutlineButton>
                      <DangerOutlineButton startIcon={<DeleteOutlineIcon fontSize="small" />} disabled={pending} onClick={onRemoveCard}>
                        {t('카테고리 제거')}
                      </DangerOutlineButton>
                    </Box>
                  </Empty>
                )
              ) : (
                <>
                  <AllRow>
                    <Label bold>{t('전체')}</Label>
                    <Right>
                      <Status active={allOn}>{allOn ? t('켜짐') : t('꺼짐')}</Status>
                      <StyledSwitch size="small" checked={allOn} disabled={pending || enabledRows.length === 0} onChange={e => onToggleAll(e.target.checked)} />
                    </Right>
                  </AllRow>
                  {rows.map(row => (
                    <Row key={row.channel} disabledRow={row.disabled}>
                      <Left>
                        <Label>{row.label}</Label>
                        <Tooltip title={t('이름 수정')}>
                          <IconButton size="small" disabled={pending} onClick={() => onEditRow(row.channel)}>
                            <EditOutlinedIcon sx={{ fontSize: 16, color: '#999' }} />
                          </IconButton>
                        </Tooltip>
                        {!isPower && (
                          <Tooltip title={t('카테고리에서 빼기')}>
                            <IconButton size="small" disabled={pending} onClick={() => onRemoveRow(row.channel)} sx={{ ml: '-6px' }}>
                              <DeleteOutlineIcon sx={{ fontSize: 16, color: '#999' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Left>
                      <Right>
                        <Status active={row.on && !row.disabled}>{row.disabled ? t('미사용') : row.on ? t('켜짐') : t('꺼짐')}</Status>
                        <StyledSwitch
                          size="small"
                          checked={row.on && !row.disabled}
                          disabled={pending || row.disabled}
                          onChange={e => onToggleRow(row.channel, e.target.checked)}
                        />
                      </Right>
                    </Row>
                  ))}
                  {!isPower && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, py: 1.5 }}>
                      <OutlineButton startIcon={<AddIcon fontSize="small" />} disabled={pending || candidates.length === 0} onClick={() => setAdding(true)}>
                        {t('전원 추가')}
                      </OutlineButton>
                      <DangerOutlineButton startIcon={<DeleteOutlineIcon fontSize="small" />} disabled={pending} onClick={onRemoveCard}>
                        {t('카테고리 제거')}
                      </DangerOutlineButton>
                    </Box>
                  )}
                </>
              )}
            </Body>
            <Footer>
              <GhostButton onClick={onClose}>{t('닫기')}</GhostButton>
            </Footer>
          </>
        )}
      </Container>
    </Modal>
  );
};

export default CategoryCardDialog;

/**
 * AddCategoryDialog - 카테고리 카드 추가 선택
 */
export const AddCategoryDialog = ({ open, onClose, roomName, existing = [], onSelect }) => {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose}>
      <Container sx={{ width: '400px' }}>
        <Header>
          <Title>{t('카테고리 추가')}</Title>
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Header>
        <Body>
          <Sub>{roomName}</Sub>
          <OptionGrid>
            {CATEGORY_CARDS.map(c => {
              const Icon = CARD_ICON[c.key] || EtcIcon;
              return (
                <OptionButton key={c.key} disabled={existing.includes(c.key)} onClick={() => onSelect(c.key)}>
                  <Icon width={22} height={22} color="#4AB3BC" />
                  {t(c.label)}
                </OptionButton>
              );
            })}
          </OptionGrid>
        </Body>
        <Footer>
          <GhostButton onClick={onClose}>{t('닫기')}</GhostButton>
        </Footer>
      </Container>
    </Modal>
  );
};
